import { jwtDecode } from 'jwt-decode';

/**
 * 小部件（/widget/:id 跨站 iframe）内的登录态。
 *
 * 跨站 iframe 里读不到 .gbase.ai 域的登录 cookie（SameSite=Lax / 第三方 cookie 拦截），
 * 所以小部件内登录拿到的 token 单独存在 iframe 自己的 sessionStorage（分区存储，关闭 Tab 即清除）。
 * 只有用户在小部件内显式登录过才会有值，匿名小部件行为不受影响。
 */
export const KEY_WIDGET_TOKEN = 'GBASE_WIDGET_TOKEN';

/** 登录弹窗 → 小部件 iframe 的 postMessage 类型 */
export const WIDGET_LOGIN_MESSAGE_TYPE = 'GBASE_WIDGET_LOGIN';

/** 登录弹窗完成后回跳的页面路径（web 端路由） */
export const WIDGET_LOGIN_CALLBACK_PATH = '/widget-login-callback';

const WIDGET_AUTH_CHANGED_EVENT = 'gbase-widget-auth-changed';

const WIDGET_PATH_REGEX = /^\/widget\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/** 当前是否是 plugin.js 嵌入的小部件页（/widget/:uuid） */
export const isWidgetPluginPath = (pathname: string = window.location.pathname): boolean =>
  WIDGET_PATH_REGEX.test(pathname);

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode(token.replace('Bearer ', ''));
    if (!decoded?.exp) return true;
    return decoded.exp * 1000 <= Date.now();
  } catch (e) {
    return true;
  }
};

const notifyWidgetAuthChanged = () => {
  window.dispatchEvent(new Event(WIDGET_AUTH_CHANGED_EVENT));
};

/** 读取小部件登录 token（含 "Bearer " 前缀）；过期自动清除并返回空串 */
export const getWidgetToken = (): string => {
  try {
    const token = sessionStorage.getItem(KEY_WIDGET_TOKEN);
    if (!token) return '';
    if (isTokenExpired(token)) {
      sessionStorage.removeItem(KEY_WIDGET_TOKEN);
      return '';
    }
    return token;
  } catch (e) {
    // sessionStorage 不可用（隐私模式）→ 视为未登录
    return '';
  }
};

export const setWidgetToken = (token: string) => {
  try {
    sessionStorage.setItem(KEY_WIDGET_TOKEN, token);
  } catch (e) {
    console.error('[widgetToken] sessionStorage 写入失败', e);
  }
  notifyWidgetAuthChanged();
};

export const clearWidgetToken = () => {
  try {
    sessionStorage.removeItem(KEY_WIDGET_TOKEN);
  } catch (e) {
    // 忽略
  }
  notifyWidgetAuthChanged();
};

/** 订阅小部件登录态变化，返回取消订阅函数 */
export const subscribeWidgetAuth = (listener: () => void): (() => void) => {
  window.addEventListener(WIDGET_AUTH_CHANGED_EVENT, listener);
  return () => window.removeEventListener(WIDGET_AUTH_CHANGED_EVENT, listener);
};
